import type { MouseEvent } from 'react';
import { useCopyToClipboard, useToggle, useUpdateEffect } from 'react-use';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import type { ButtonProps } from '@mui/material';
import { Button } from '@mui/material';

import type { ActionIconButtonProps } from '@/components/buttons/ActionIconButton';
import { ActionIconButton } from '@/components/buttons/ActionIconButton';
import { ReactComponent as DoneSVG } from '@/shared/assets/icons-2.0/done.svg';

// ----------------------------------------------------------------------

type Props = ActionIconButtonProps & {
  valueToCopy: string;
  label?: string;
};

// ----------------------------------------------------------------------

export const ClipboardButton = (props: Props) => {
  const { valueToCopy, onClick: passedOnClick, label, ...restOfProps } = props;

  const [, copyToClipboard] = useCopyToClipboard();
  const [isCopied, toggleIsCopied] = useToggle(false);

  const showCopiedIcon = () => toggleIsCopied(true);
  const hideCopiedIcon = () => toggleIsCopied(false);

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    copyToClipboard(valueToCopy);
    passedOnClick?.(e);
    showCopiedIcon();
  };

  useUpdateEffect(() => {
    if (isCopied) {
      const timeout = setTimeout(hideCopiedIcon, 1000);

      return () => clearTimeout(timeout);
    }
  }, [isCopied]);

  const ButtonIcon = isCopied ? <DoneSVG fontSize="medium" /> : <ContentCopyIcon fontSize="medium" />;

  return (
    <ActionIconButton title="Скопировать" onClick={handleClick} {...restOfProps}>
      {ButtonIcon}
      {label}
    </ActionIconButton>
  );
};

type ClipboardTextButtonProps = ButtonProps & {
  valueToCopy: string;
  label?: string;
  copiedLabel?: string;
};

export const ClipboardTextButton = (props: ClipboardTextButtonProps) => {
  const { valueToCopy, onClick: passedOnClick, label, copiedLabel, ...restOfProps } = props;

  const [, copyToClipboard] = useCopyToClipboard();
  const [isCopied, toggleIsCopied] = useToggle(false);

  const showCopiedIcon = () => toggleIsCopied(true);
  const hideCopiedIcon = () => toggleIsCopied(false);

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    copyToClipboard(valueToCopy);
    passedOnClick?.(e);
    showCopiedIcon();
  };

  useUpdateEffect(() => {
    if (isCopied) {
      const timeout = setTimeout(hideCopiedIcon, 1000);

      return () => clearTimeout(timeout);
    }
  }, [isCopied]);

  const buttonIcon = isCopied ? <DoneSVG fontSize="medium" /> : <ContentCopyIcon fontSize="medium" />;
  const buttonLabel = copiedLabel && isCopied ? copiedLabel : label;

  return (
    <Button variant="contained" color="secondary" startIcon={buttonIcon} onClick={handleClick} {...restOfProps}>
      {buttonLabel}
    </Button>
  );
};
